import { useMemo } from 'react';
import { OptionKey, useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import _ from 'lodash';

import { api } from '@/api';
import { DEFAULT_DATE_FORMAT, RAW_DATE_FORMAT } from '@/common/constants';
import { Pagination } from '@/common/types';
import { API_URL } from '@/dtos';
import { ReportTab, ReportTypeKeys } from '@/modules/Reports/report.types';

import {
  PER_PAGE,
  ScheduledFiltersResponse,
  ScheduledReportInboxItemPayload,
} from '../../ScheduledReport.types';

export const GET_SCHEDULERS_INBOX_QUERY_KEY = 'GET_SCHEDULERS_INBOX_QUERY_KEY';

export interface ScheduledResponse {
  id: number;
  name: string;
  report_type: ReportTypeKeys;
  report_group_type: ReportTab;
  generation_date: string;
  file_url: string;
  plan_names?: string[];

  report_filter: Partial<ScheduledFiltersResponse>;
}
interface Params {
  searchValue: string;
  types?: OptionKey[];
  statuses?: OptionKey[];
  page: number;
  perPage: number;
}
const useGetScheduledInboxReportsQuery = ({ searchValue, types, page = 1, perPage, statuses }: Params) => {
  const { isLoading, isSuccess, data } = useQuery(
    [GET_SCHEDULERS_INBOX_QUERY_KEY, page, perPage, searchValue, types?.join(','), statuses?.join(',')],
    () =>
      // TODO: the type is missing
      api.get<Pagination<ScheduledResponse>>(API_URL.REPORTS_RESULTS, {
        params: _.pickBy({
          search_value: searchValue,
          types: types ? types : undefined,
          statuses: statuses ? statuses : undefined,
          page: page - 1,
          size: perPage || PER_PAGE,
        }),
      })
  );
  const formattedData = useMemo(() => {
    const currentList = data?.data?.content || [];
    return currentList.map((currentDate, index) => {
      const filters = currentDate?.report_filter;
      const asDate = filters?.as_of_date;
      const planIds = filters?.plan_ids ? filters?.plan_ids.map((item) => `${item}`) : undefined;
      const item: ScheduledReportInboxItemPayload = {
        id: `${currentDate.id || index}`,
        name: currentDate?.name || '',
        reportType: currentDate?.report_type || null,
        reportGroupType: currentDate?.report_group_type,
        generationDate: currentDate.generation_date
          ? dayjs(currentDate.generation_date, RAW_DATE_FORMAT).format(DEFAULT_DATE_FORMAT)
          : '',
        url: currentDate.file_url,
        reportFilter: _.pickBy({
          employerId: filters?.employer_id,
          isSSNShowed: filters?.show_ssn,
          cardStatusReportGroupType: filters?.card_status_report_group_type,
          isNotMailed: filters?.is_not_mailed,
          cardStatuses: filters?.card_statuses,
          asDate: asDate ? dayjs(asDate, RAW_DATE_FORMAT).toDate() : undefined,
          accountStatuses: filters?.account_statuses,
          employmentStatuses: filters?.employment_statuses,
          enrollmentStatuses: filters?.enrollment_statuses,
          cipStatuses: filters?.cip_statuses,
          claimStatuses: filters?.claim_statuses,
          claimTypes: filters?.claim_types,
          invoiceId: filters?.invoice_id,
          forfeitureReportGroupType: filters?.forfeiture_report_group_type,
          planYearPeriod: filters?.plan_year_period,
          reportPlanIds: planIds,
          planIds: planIds,
          planNames: currentDate?.plan_names,
          activityDateRangeFor: filters?.claim_activity_date_range_type,
        }),
      };
      return item;
    });
  }, [data?.data]);
  const total = useMemo(() => {
    return data?.data?.totalElements || 0;
  }, [data?.data?.totalElements]);

  return {
    total,
    isSuccess,
    data: formattedData,
    isLoading,
  };
};

export default useGetScheduledInboxReportsQuery;
